import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Switch,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Close, Height } from '@material-ui/icons';
import { Container, Draggable, DropResult } from 'react-smooth-dnd';
import arrayMove from 'array-move';
import { findIndex } from 'lodash';
import { IAvailableColumnModel, IAvailableGroupColumnModel } from 'interfaces';

interface IVisibleDialogProps {
  groupColumns?: IAvailableGroupColumnModel[];
  visibleItems: IAvailableColumnModel[];
  useFilterCriteria?: boolean;
  defaultValues?: { filters: IAvailableColumnModel[]; columns: IAvailableColumnModel[] };
  hideFilters?: string[];
  hideColumns?: string[];
  onSave: (visibleItems: IAvailableColumnModel[]) => void;
  onClose: () => void;
}

const VisibilityDialog: React.FC<IVisibleDialogProps & DialogProps> = (props) => {
  const { open, groupColumns, useFilterCriteria = false, hideFilters = [], hideColumns = [], onSave, onClose } = props;

  const [visibleItems, setVisibleItems] = useState<IAvailableColumnModel[]>(props.visibleItems || []);
  const visibleInitialItems: IAvailableColumnModel[] = props.visibleItems || [];

  const onDrop = ({ removedIndex, addedIndex }: DropResult) => {
    setVisibleItems((prevVisibleItems: IAvailableColumnModel[]) =>
      arrayMove(prevVisibleItems, removedIndex!, addedIndex!),
    );
  };

  const handleChangeSwitch = (column: IAvailableColumnModel) => {
    const inArrayIndex = findIndex(visibleItems, (item) => item.columnName === column.columnName);

    if (inArrayIndex > -1)
      setVisibleItems(visibleItems.filter((item: IAvailableColumnModel) => item.columnName !== column.columnName));
    else setVisibleItems((prevVisibleItems: IAvailableColumnModel[]) => [...prevVisibleItems, column]);
  };

  const handleResetItems = () => {
    if (props.defaultValues) {
      setVisibleItems(useFilterCriteria ? props.defaultValues['filters'] : props.defaultValues['columns']);
      onSave(useFilterCriteria ? props.defaultValues['filters'] : props.defaultValues['columns']);
    }
  };

  const handleCloseDialog = () => {
    setVisibleItems(visibleInitialItems);
    onClose();
  };

  const renderBlocks = (columnGroup: IAvailableGroupColumnModel, groupIndex: number, isFilter: boolean) => {
    if (!columnGroup.columns.length) return null;

    return (
      <Grid item key={groupIndex} xs={5}>
        <Paper>
          <Box sx={{ p: '20px 20px 0' }}>
            <Typography component="h4" variant="h5">
              {columnGroup.caption}
            </Typography>
          </Box>

          <List>
            {columnGroup.columns.map((column, columnIndex) => {
              if (useFilterCriteria && !column.filterCriteriaList.length) return null;
              if (!useFilterCriteria && column.caption !== column.filterCaption) return null;
              if (hideFilters.includes(column.columnName)) return null;
              if (hideColumns.includes(column.columnName)) return null;

              return (
                <ListItem key={column.columnName} divider={columnIndex !== columnGroup.columns.length - 1}>
                  <ListItemText primary={isFilter ? column.filterCaption : column.caption} />

                  <ListItemSecondaryAction>
                    <Switch
                      color="primary"
                      checked={
                        findIndex(
                          visibleItems,
                          (item: IAvailableColumnModel) => column.columnName === item.columnName,
                        ) > -1
                      }
                      onChange={() => handleChangeSwitch(column)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Grid>
    );
  };

  return (
    <Dialog open={open} fullScreen onClose={handleCloseDialog}>
      <AppBar position="relative">
        <Toolbar>
          <Typography variant="h6">{useFilterCriteria ? 'Настройки фильтров' : 'Настройки колонок'}</Typography>

          <Box marginLeft="auto">
            <Button
              onClick={() => onSave(visibleItems)}
              style={{ marginRight: 24, backgroundColor: '#27ae60', color: 'white' }}
              variant="contained"
            >
              Сохранить
            </Button>

            <IconButton edge="start" color="inherit" onClick={handleCloseDialog} aria-label="close">
              <Close />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Typography component="h2" variant="h4" gutterBottom>
              {useFilterCriteria ? 'Фильтры' : 'Колонки'}
            </Typography>

            <Grid container spacing={2}>
              {(groupColumns || []).map((columnGroup, groupIndex) => {
                return useFilterCriteria
                  ? columnGroup.columns.filter((item) => item.filterCriteriaList.length > 0).length > 0 &&
                      renderBlocks(columnGroup, groupIndex, true)
                  : renderBlocks(columnGroup, groupIndex, false);
              })}
            </Grid>
          </Grid>

          <Grid item xs={4}>
            <Box sx={{ position: 'sticky', top: 0 }}>
              <Typography component="h3" variant="h4">
                Порядок столбцов
              </Typography>

              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Перетаскивайте строки вверх и вниз в нужной для вас последовательности
              </Typography>

              <Paper>
                <Box sx={{ maxHeight: 'calc(100vh - 240px)' }} style={{ overflowY: 'auto' }}>
                  <List>
                    <Container dragHandleSelector="#drag-handle" lockAxis="y" onDrop={onDrop}>
                      {visibleItems.map((columnsOrder, columnsOrderIndex) => (
                        <Draggable key={columnsOrderIndex}>
                          <ListItem divider={columnsOrderIndex !== visibleItems.length - 1}>
                            <ListItemText primary={columnsOrder.filterCaption} />

                            <ListItemSecondaryAction>
                              <Box id="drag-handle" style={{ cursor: 'grab' }}>
                                <Height />
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </Draggable>
                      ))}
                    </Container>
                  </List>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {false && (
        <DialogActions style={{ justifyContent: 'flex-start' }}>
          <ButtonGroup variant="outlined">
            <Button onClick={() => onSave(visibleItems)}>Сохранить</Button>

            {props.defaultValues && <Button onClick={handleResetItems}>По умолчанию</Button>}

            <Button onClick={handleCloseDialog}>Отменить</Button>
          </ButtonGroup>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default VisibilityDialog;
